import { useEffect, useRef, useState } from "react";
import { Characteristic, Schema, EstimatedCharacteristicsReport, hasCharacteristicAnswer } from "../types";
import Title from "./ui/Title";
import Button from "./ui/Button";
import { IconLoader2, IconPencil, IconSearch, IconChevronUp, IconChevronDown, IconArrowsSort } from "@tabler/icons-react";
import CharacteristicEstimationCorrectionModal from "./CharacteristicEstimationCorrect";

interface CharacteristicsProps {
    editableCharacteristics: Record<string, Characteristic>;
    setEditableCharacteristics: React.Dispatch<React.SetStateAction<Record<string, Characteristic>>>;
    typedSchema: Schema;
    estimateCharacteristics: () => void;
    estimationLoading: boolean;
    estimatedCharacteristicsReport: EstimatedCharacteristicsReport | null;
    simulation_token: string | null;
    isFullscreen?: boolean;
}

type SortColumn = "name" | "answer" | "explanation" | "confidence" | "importance";

const Characteristics = ({editableCharacteristics, setEditableCharacteristics, typedSchema, estimateCharacteristics, estimationLoading, estimatedCharacteristicsReport, simulation_token, isFullscreen}: CharacteristicsProps) => {
    const characteristicsContainerRef = useRef<HTMLDivElement>(null);

    const [characteristicsAtBottom, setCharacteristicsAtBottom] = useState(false);
    const [expandedTooltip, setExpandedTooltip] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
    const [filterUnestimated, setFilterUnestimated] = useState(false);
    const [correctionCommentKey, setCorrectionCommentKey] = useState<string | null>(null);
    const [correctionModalKey, setCorrectionModalKey] = useState<string | null>(null);

    useEffect(() => {
        const handleCharacteristicsScroll = () => {
            const container = characteristicsContainerRef.current;
            if (container) {
                const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 5;
                setCharacteristicsAtBottom(isAtBottom);
            }
        };

        const characteristicsContainer = characteristicsContainerRef.current;

        if (characteristicsContainer) {
            characteristicsContainer.addEventListener('scroll', handleCharacteristicsScroll);
            handleCharacteristicsScroll();
        }

        return () => {
            if (characteristicsContainer) {
                characteristicsContainer.removeEventListener('scroll', handleCharacteristicsScroll);
            }
        };
    }, []);

    useEffect(() => {
        const characteristicsContainer = characteristicsContainerRef.current;
        if (characteristicsContainer) {
            const isAtBottom = characteristicsContainer.scrollHeight - characteristicsContainer.scrollTop <= characteristicsContainer.clientHeight + 5;
            setCharacteristicsAtBottom(isAtBottom);
        }
    }, [editableCharacteristics]);

    const isNumeric = (value: string | number | null): boolean => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'number') return true;
        const numStr = String(value).trim();
        return numStr !== '' && !isNaN(Number(numStr));
    };

    const compareValues = (a: string | number | null, b: string | number | null, direction: "asc" | "desc"): number => {
        const aValue = a === null || a === undefined ? null : a;
        const bValue = b === null || b === undefined ? null : b;

        const aIsNumeric = isNumeric(aValue);
        const bIsNumeric = isNumeric(bValue);

        const shouldCompareAsNumbers = (aValue !== null && aIsNumeric) || (bValue !== null && bIsNumeric);

        if (shouldCompareAsNumbers) {
            const numA = aValue === null ? 0 : (typeof aValue === 'number' ? aValue : Number(aValue));
            const numB = bValue === null ? 0 : (typeof bValue === 'number' ? bValue : Number(bValue));
            return direction === "asc" ? numA - numB : numB - numA;
        }

        const strA = aValue === null ? '' : String(aValue).toLowerCase();
        const strB = bValue === null ? '' : String(bValue).toLowerCase();
        if (strA < strB) return direction === "asc" ? -1 : 1;
        if (strA > strB) return direction === "asc" ? 1 : -1;
        return 0;
    };

    const getSortedCharacteristics = () => {
        let filtered = Object.entries(editableCharacteristics);

        if (filterUnestimated) {
            filtered = filtered.filter(([, value]) =>
                hasCharacteristicAnswer(value.answer) && value.confidence !== null
            );
        }

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(([key]) =>
                key.toLowerCase().includes(searchLower) ||
                (typedSchema[key]?.description_value?.toLowerCase().includes(searchLower) ?? false)
            );
        }

        if (sortColumn) {
            filtered = filtered.sort(([nameA, valueA], [nameB, valueB]) => {
                let a: string | number | null;
                let b: string | number | null;

                switch (sortColumn) {
                    case "name":
                        a = nameA;
                        b = nameB;
                        break;
                    case "answer":
                        a = valueA.answer;
                        b = valueB.answer;
                        break;
                    case "explanation":
                        a = valueA.explanation;
                        b = valueB.explanation;
                        break;
                    case "confidence":
                        a = valueA.confidence;
                        b = valueB.confidence;
                        break;
                    case "importance":
                        a = valueA.importance;
                        b = valueB.importance;
                        break;
                    default:
                        return 0;
                }

                return compareValues(a, b, sortDirection);
            });
        } else {
            filtered = filtered.sort(([nameA, valueA], [nameB, valueB]) => {
                const aHas = hasCharacteristicAnswer(valueA.answer);
                const bHas = hasCharacteristicAnswer(valueB.answer);
                if (aHas && !bHas) return -1;
                if (!aHas && bHas) return 1;

                const importanceA = valueA.importance || 0;
                const importanceB = valueB.importance || 0;
                if (importanceA !== importanceB) {
                    return importanceB - importanceA;
                }

                if (nameA.includes('Meldingsclassificatie') && !nameB.includes('Meldingsclassificatie')) return -1;
                if (!nameA.includes('Meldingsclassificatie') && nameB.includes('Meldingsclassificatie')) return 1;

                return 0;
            });
        }

        return filtered;
    };

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const handleCharacteristicChange = (key: string, field: 'answer' | 'explanation' | 'confidence' | 'importance' | 'corrected_by' | 'comment' | 'correction_count', value: string | number | null) => {
        setEditableCharacteristics(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value,
                isEdited: true
            }
        }));
    };

    const renderConfidence = (value: { confidence: number | null }) => {
        if (value.confidence === null) {
            return <span className="text-xs italic text-gray-500">(Not estimated)</span>;
        }

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                value.confidence >= 0.7 ? 'bg-green-100 text-green-800' :
                value.confidence >= 0.4 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
            }`}>
                {Math.round(value.confidence * 100)}%
            </span>
        );
    };

    const renderTruncatedCharacteristic = (key: string, value: Characteristic) => {
        const description = typedSchema[key]?.description_value;
        const isExpanded = expandedTooltip === key;

        return (
            <div className="relative">
                <div className="flex items-start gap-1">
                    <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 break-words">{key}</h4>
                        {description && (
                            <p className={`text-xs text-gray-500 mt-1 ${isExpanded ? '' : 'line-clamp-1'}`}>
                                {description}
                            </p>
                        )}
                        {value.corrected_by && (
                            <button
                                type="button"
                                className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[8px] font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCorrectionCommentKey(prev => (prev === key ? null : key));
                                }}
                                title={value.comment || 'View correction comment'}
                            >
                                Corrected by: {value.corrected_by}{(value.correction_count ?? 0) > 1 ? ` (${value.correction_count})` : ''}
                            </button>
                        )}
                        {correctionCommentKey === key && value.comment && (
                            <p className="text-xs text-gray-500 mt-1 italic">
                                &ldquo;{value.comment}&rdquo;
                            </p>
                        )}
                    </div>
                    {description && (
                        <button
                            type="button"
                            onClick={() => setExpandedTooltip(isExpanded ? null : key)}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            aria-label={isExpanded ? 'Inklappen' : 'Uitklappen'}
                            title={isExpanded ? 'Inklappen' : 'Uitklappen'}
                        >
                            {isExpanded ? <IconChevronUp className="w-4 h-4" /> : <IconChevronDown className="w-4 h-4" />}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderSortIcon = (column: SortColumn) => {
        if (sortColumn !== column) {
            return <IconArrowsSort className="w-4 h-4 opacity-30" />;
        }
        return sortDirection === "asc"
            ? <IconChevronUp className="w-4 h-4" />
            : <IconChevronDown className="w-4 h-4" />;
    };

    const estimatedCharacteristicsCount = Object.values(editableCharacteristics).filter(char => hasCharacteristicAnswer(char.answer)).length;
    const totalCharacteristics = Object.keys(typedSchema).length;
    const estimatedPercentage = totalCharacteristics > 0
        ? Math.round((estimatedCharacteristicsCount / totalCharacteristics) * 100)
        : 0;

    const confidenceValues = Object.values(editableCharacteristics)
        .filter(char => char.confidence !== null)
        .map(char => char.confidence as number);
    const aggregatedConfidence = confidenceValues.length > 0
        ? confidenceValues.reduce((sum, c) => sum + c, 0) / confidenceValues.length
        : null;
    const aggregatedConfidencePercentage = aggregatedConfidence === null ? null : Math.round(aggregatedConfidence * 100);

    const ingeschatBoxClass =
        estimatedPercentage >= 70 ? 'bg-green-50 border-green-200 text-green-800' :
        estimatedPercentage >= 40 ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
        'bg-red-50 border-red-200 text-red-800';

    const vertrouwenBoxClass =
        aggregatedConfidencePercentage === null ? 'bg-gray-50 border-gray-200 text-gray-700' :
        aggregatedConfidencePercentage >= 70 ? 'bg-green-50 border-green-200 text-green-800' :
        aggregatedConfidencePercentage >= 40 ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
        'bg-red-50 border-red-200 text-red-800';

    const canEditEstimation = Boolean(
        simulation_token || estimatedCharacteristicsReport?.characteristic_event_id
    );
    const correctionModalValue = correctionModalKey ? editableCharacteristics[correctionModalKey] : null;

    return (
        <div className={`w-full flex flex-col min-h-0 ${isFullscreen ? 'h-full' : 'h-full'}`}>
            <div className="flex flex-row items-center justify-between gap-3 flex-shrink-0 mb-3">
                <div className="flex flex-row items-center gap-2">
                    <Title title="MeldingsClassificaties (LMC)" />
                    {estimationLoading && <IconLoader2 className="w-4 h-4 animate-spin text-gray-500" />}
                </div>
                <div className="flex items-center gap-3">
                    <label htmlFor="filter-unestimated" className="flex items-center gap-2 cursor-pointer select-none">
                        <span className="text-xs text-gray-600">Verberg ongeschat</span>
                        <button
                            id="filter-unestimated"
                            type="button"
                            role="switch"
                            aria-checked={filterUnestimated}
                            onClick={() => setFilterUnestimated(!filterUnestimated)}
                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${filterUnestimated ? 'bg-blue-600' : 'bg-gray-300'}`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${filterUnestimated ? 'translate-x-4' : 'translate-x-0.5'}`}
                            />
                        </button>
                    </label>
                    <Button onClick={estimateCharacteristics}>Update</Button>
                </div>
            </div>

            <div className="flex flex-row gap-2 items-stretch flex-shrink-0 mb-3">
                <div className={`flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md border text-sm font-medium ${ingeschatBoxClass}`}>
                    Ingeschat: {estimatedCharacteristicsCount} / {totalCharacteristics} ({estimatedPercentage}%)
                </div>
                <div className={`flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md border text-sm font-medium ${vertrouwenBoxClass}`}>
                    Vertrouwen: {aggregatedConfidencePercentage === null ? '—' : `${aggregatedConfidencePercentage}%`}
                </div>
            </div>

            <div className="flex flex-row gap-2 w-full items-center mb-3 flex-shrink-0">
                <div className="relative flex-1">
                    <IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Zoek in Karakteristiek..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                </div>
                <Button onClick={() => { /* live filter — button for consistency */ }}>
                    Zoek
                </Button>
            </div>

            <div className="flex-1 min-h-0 relative">
                <div
                    ref={characteristicsContainerRef}
                    className="h-full overflow-y-auto"
                >
                    <div className="min-w-full">
                        <div className="bg-gray-50 sticky top-0 z-10">
                            <div className="flex text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                                <div
                                    className="px-3 py-3 w-48 flex-shrink-0 cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1 select-none"
                                    onClick={() => handleSort("name")}
                                >
                                    Karakteristiek
                                    {renderSortIcon("name")}
                                </div>
                                <div
                                    className="px-4 py-3 w-32 flex-shrink-0 cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1 select-none"
                                    onClick={() => handleSort("answer")}
                                >
                                    Antwoord
                                    {renderSortIcon("answer")}
                                </div>
                                <div
                                    className="px-4 py-3 flex-1 min-w-0 cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1 select-none"
                                    onClick={() => handleSort("explanation")}
                                >
                                    Toelichting
                                    {renderSortIcon("explanation")}
                                </div>
                                <div
                                    className="px-4 py-3 w-24 flex-shrink-0 cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1 select-none"
                                    onClick={() => handleSort("confidence")}
                                >
                                    Vertrouwen
                                    {renderSortIcon("confidence")}
                                </div>
                                <div
                                    className="px-4 py-3 w-24 flex-shrink-0 cursor-pointer hover:bg-gray-100 transition-colors flex items-center gap-1 select-none"
                                    onClick={() => handleSort("importance")}
                                >
                                    Urgentie
                                    {renderSortIcon("importance")}
                                </div>
                            </div>
                        </div>
                        <div className="bg-white">
                            {getSortedCharacteristics().map(([characteristicKey, value]) => (
                                <div key={characteristicKey} className="flex items-start hover:bg-gray-50 transition-colors border-b border-gray-100">
                                    <div className="px-3 py-4 w-48 flex-shrink-0">
                                        {renderTruncatedCharacteristic(characteristicKey, value)}
                                    </div>
                                    <div className="px-4 py-4 w-32 flex-shrink-0">
                                        <div className="flex items-start gap-1">
                                            <textarea
                                                value={value.answer}
                                                onChange={(e) => handleCharacteristicChange(characteristicKey, 'answer', e.target.value)}
                                                className="flex-1 min-w-0 p-2 border border-gray-300 rounded text-sm resize-none"
                                                rows={3}
                                            />
                                            {canEditEstimation && (
                                                <button
                                                    type="button"
                                                    onClick={() => setCorrectionModalKey(characteristicKey)}
                                                    className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                                    aria-label="Correct or create estimation"
                                                    title="Correct estimation"
                                                >
                                                    <IconPencil className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="px-4 py-4 flex-1 min-w-0">
                                        <textarea
                                            value={value.explanation}
                                            onChange={(e) => handleCharacteristicChange(characteristicKey, 'explanation', e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
                                            rows={3}
                                        />
                                    </div>
                                    <div className="px-4 py-4 w-24 flex-shrink-0">
                                        {renderConfidence(value)}
                                    </div>
                                    <div className="px-4 py-4 w-24 flex-shrink-0">
                                        {value.importance !== null && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 w-fit">
                                                {value.importance}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {!characteristicsAtBottom && (
                    <div className="absolute bottom-0 left-0 right-0 h-24 w-full pointer-events-none bg-gradient-to-t from-white to-transparent" />
                )}
            </div>

            {correctionModalValue && correctionModalKey && (
                <CharacteristicEstimationCorrectionModal
                    isOpen={true}
                    onClose={() => setCorrectionModalKey(null)}
                    onSuccess={handleCharacteristicChange}
                    characteristicKey={correctionModalKey}
                    characteristicSchema={typedSchema[correctionModalKey]}
                    answer={correctionModalValue.answer}
                    explanation={correctionModalValue.explanation}
                    confidence={correctionModalValue.confidence}
                    importance={correctionModalValue.importance}
                    simulation_token={simulation_token}
                    estimatedCharacteristicsReport={estimatedCharacteristicsReport}
                />
            )}
        </div>
    );
};

export default Characteristics;
